import { createAsyncThunk } from "@reduxjs/toolkit";

import * as addressAPI from "../../../api/address";
import { IAddressCountry } from "../../../models/address";

/**
 * Return type for loadCountries thunk.
 */
interface LoadCountriesResult {
    countries: IAddressCountry[];
    initialCountryURL: string;
}

/**
 * Load available shipping countries.
 *
 * Fetches all countries from the API and filters to only shipping-enabled countries.
 * Sets the initial country selection to the first available shipping country.
 * This data is used to populate country dropdowns in address forms.
 *
 * @returns {Promise<LoadCountriesResult>} Promise resolving to shipping countries and initial selection
 *
 * @example
 * // Load countries on checkout page mount
 * dispatch(loadCountries());
 *
 * @sideEffects
 * - Updates `data.countries` with filtered shipping countries via extraReducers
 * - Updates `form.shipping_country` with initial country URL via extraReducers
 * - Updates `form.billing_country` with initial country URL via extraReducers
 * - Logs to console in debug mode
 */
export const loadCountries = createAsyncThunk(
    "checkout/loadCountries",
    async (): Promise<LoadCountriesResult> => {
        console.debug("Loading countries...");

        const countries = await addressAPI.loadCountries();

        // Ignore non-shipping countries
        const shippingCountries = countries.filter((country) => {
            return country.is_shipping_country;
        });

        // Set default value
        const initialCountryURL = shippingCountries.length
            ? shippingCountries[0].url
            : "";

        console.debug("Loaded countries.");

        return {
            countries: shippingCountries,
            initialCountryURL,
        };
    },
);
